/**
 * StatisticCard - 统计卡片组件
 * 包含主统计数据、子统计项和操作按钮
 */
import { useEffect, useRef, useState } from "react";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { handleHttp } from "@/components/Menu/ProCard/utils/valueConfig";
import { DEFAULT_DATA } from "@/config";
import Features from "../Features";
import "./index.less";

export interface StatisticItemProps {
  label?: string;
  labelTooltip?: string;
  value?: string | number;
  valueKey?: string;
  valueStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  colSpan?: number;
}

export interface StatisticCardProps {
  title?: string;
  titleTooltip?: string;
  value?: string | number;
  valueConfig?: any;
  valueKey?: string;
  itemsValueConfig?: any;
  items?: StatisticItemProps[];
  titleStyle?: React.CSSProperties;
  valueStyle?: React.CSSProperties;
  wrapStyle?: React.CSSProperties;
  updateKey?: number | undefined;
  /**
   * 轮询间隔（秒），大于 0 时启用定时刷新
   */
  needInterval?: number;
  subFeatures?: string[];
}

const StatisticCardPrefix = "iot-component-statistic-card";

const StatisticCard = (props: StatisticCardProps) => {
  const {
    title = "",
    titleTooltip = "",
    value = DEFAULT_DATA,
    valueConfig = null,
    valueKey = "",
    itemsValueConfig = null,
    items = [],
    valueStyle = {},
    wrapStyle = {},
    updateKey,
    needInterval,
    subFeatures = [],
  } = props;

  const [result, setResult] = useState<any>(
    typeof value === "object" ? value : { [valueKey || "value"]: value }
  );

  const [itemsResult, setItemsResult] = useState<any>(null);

  const itemsValueConfigRef = useRef(itemsValueConfig);
  itemsValueConfigRef.current = itemsValueConfig;

  const updateData = () => {
    if (valueConfig && valueConfig.type === "http") {
      handleHttp(valueConfig, (resultIn: any) => {
        setResult(resultIn);
        if (!itemsValueConfigRef.current) {
          setItemsResult(resultIn);
        }
      });
    }

    if (itemsValueConfig && itemsValueConfig.type === "http") {
      handleHttp(itemsValueConfig, (resultIn: any) => {
        setItemsResult(resultIn);
      });
    }
  };

  const pollData = () => {
    if (valueConfig?.type === "http") {
      handleHttp(valueConfig, (resultIn: any) => {
        setResult(resultIn);
        if (!itemsValueConfigRef.current) {
          setItemsResult(resultIn);
        }
      });
    }
    if (itemsValueConfig?.type === "http") {
      handleHttp(itemsValueConfig, (resultIn: any) => {
        setItemsResult(resultIn);
      });
    }
  };

  useEffect(() => {
    updateData();
  }, [updateKey]);

  useEffect(() => {
    if (!needInterval || needInterval <= 0) return;
    const hasHttp =
      valueConfig?.type === "http" || itemsValueConfig?.type === "http";
    if (!hasHttp) return;
    const timer = setInterval(pollData, needInterval * 1000);
    return () => clearInterval(timer);
  }, [needInterval, valueConfig, itemsValueConfig]);

  const mainValue =
    valueKey && result
      ? result[valueKey]
      : typeof result === "object"
      ? DEFAULT_DATA
      : result;

  const itemsDataSource = itemsResult || result;

  return (
    <div className={`${StatisticCardPrefix}`} style={wrapStyle}>
      <div className={`${StatisticCardPrefix}-container`}>
        {/* 左侧：主数值和标签 */}
        <div className={`${StatisticCardPrefix}-main`}>
          <div
            className={`${StatisticCardPrefix}-main-value`}
            style={valueStyle}
          >
            {mainValue}
          </div>
          {title && (
            <div className={`${StatisticCardPrefix}-main-label`}>
              {title}
              {titleTooltip && (
                <Tooltip title={titleTooltip}>
                  <InfoCircleOutlined 
                    className={`${StatisticCardPrefix}-main-label-icon`}
                  />
                </Tooltip>
              )}
            </div>
          )}
          {subFeatures.length > 0 && (
            <div className={`${StatisticCardPrefix}-main-features`}>
              {subFeatures.map((feature, index) => {
                return <Features key={index} value={feature} />;
              })}
            </div>
          )}
        </div>

        {/* 右侧：统计项列表 */}
        {items && items.length > 0 && (
          <div className={`${StatisticCardPrefix}-items`}>
            {items.map((item, index) => {
              const itemValue =
                item.valueKey && itemsDataSource
                  ? itemsDataSource[item.valueKey]
                  : item.value;
              const colSpan = item.colSpan || 24;
              const widthPercent = (colSpan / 24) * 100;

              return (
                <div
                  key={index}
                  className={`${StatisticCardPrefix}-item`}
                  style={{ width: `${widthPercent}%` }}
                >
                  <div className={`${StatisticCardPrefix}-item-row`}>
                    <span
                      className={`${StatisticCardPrefix}-item-label`}
                      style={item.labelStyle}
                    >
                      {item.label}
                      {item.labelTooltip && (
                        <Tooltip title={item.labelTooltip}>
                          <InfoCircleOutlined 
                            className={`${StatisticCardPrefix}-item-label-icon`}
                          />
                        </Tooltip>
                      )}
                    </span>
                    <span
                      className={`${StatisticCardPrefix}-item-value`}
                      style={item.valueStyle}
                    >
                      {itemValue ?? DEFAULT_DATA}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatisticCard;
